/**
 * `.TTM` execution core (docs/formats/ttm-animation.md's "Execution
 * semantics" section) — turns a disassembled `TtmScript` (ttm.ts) into a
 * declarative, per-tick command stream. Pure: no pixels, no audio, no DOM —
 * `createTtmRun`/`advance` only produce data describing what a renderer
 * *should* do; a later lane (the canvas player) is the one that actually
 * draws or plays anything. Command/state types live in
 * ttm-interpreter-types.ts, per-opcode handlers in ttm-interpreter-ops.ts —
 * this file is just the run loop.
 *
 * Why this is a linear player, not a general DGDS scheduler: ScummVM's
 * `dgds` engine runs TTM sequences under an `.ADS` script that can
 * multiplex several sequences, loop them, and jump between frames (`GOTO`/
 * `GOSUB`, `engines/dgds/ads.cpp`). **Red Baron ships no `.ADS`/`.ADL`/
 * `.ADH` file** (docs/formats/ttm-animation.md's "`.ADS` — not present"
 * section), and the opcode census (`packages/formats/test/ttm.test.ts`'s
 * `EXPECTED_CENSUS`) confirms `0x1200 GOTO` and `0x3000 GOSUB` occur
 * **zero** times across all 28 real `.TTM` files. So the real engine must
 * just run each script's instructions start to finish, once, in order —
 * `advance()` is a straight-line walk over `script.instructions`, never
 * branches, and `0x1200`/`0x3000` (should they ever appear in a future/
 * unseen file) fall through to the generic `{kind:"unknown"}` case like any
 * other REPORTED-but-unverified opcode, rather than attempting DGDS's
 * tag-indexed frame lookup (`findGOTOTarget`) that only makes sense with
 * `.ADS` sequencing around it.
 *
 * Timing model — INFERRED, one measured data point corroborates it:
 * `0x1020 SET DELAY n` is dgds's only per-instruction timing primitive
 * (`ttm.cpp`'s case converts it via `MS_PER_FRAME = 16.6667` — a 60 Hz
 * frame, `dgds.h`). In dgds that value is *not* applied by the TTM
 * interpreter itself — it's handed to the `.ADS` scheduler
 * (`setScriptDelay`), which persists it on the running sequence
 * (`ads.cpp`'s `seq->_timeInterval`) until a later `SET DELAY` changes it,
 * i.e. a TTM frame with no `SET DELAY` of its own keeps whichever delay was
 * last set. Since Red Baron has no `.ADS` layer to host that persistence,
 * this module ports the same behaviour directly: `TtmRunState.delayFrames`
 * carries forward across ticks and is only overwritten when `0x1020`
 * executes. This specific piece (delay *persisting* across frames, vs.
 * resetting to 0 every frame) is an INFERRED port, not independently
 * confirmed for Red Baron's own executable — flagged in
 * docs/formats/ttm-animation.md's Open questions.
 *
 * The 60 Hz unit conversion itself has one directly-measured corroboration:
 * `docs/game/ui-reference/intro.md`'s live DOSBox-X capture measured the
 * held, finished `TITLE.SCR` title card at **≈5.7 s** wall clock
 * (`cycles=25000` tier); `TITLE.TTM`'s frame 36 sets `SET DELAY 330` right
 * before that hold — the single longest delay in the script. `330 ×
 * 16.6667 ms ≈ 5500 ms`, within ~4% of the measured figure. That is the
 * only tick in the whole file with an independent wall-clock cross-check;
 * every other delay value is carried from dgds by analogy and should be
 * read as a *relative* weight (exactly the caution intro.md's own frame
 * table already states), not a precise millisecond claim.
 *
 * Command coverage: every opcode the real 28-file corpus actually emits
 * (the 39 rows of docs/formats/ttm-animation.md's opcode census) has an
 * explicit handler (ttm-interpreter-ops.ts), plus a handful of
 * REPORTED-only opcodes come along for free (the wipe family, `COPY
 * BUFFER`, `SET CLIP WINDOW`, flip-mode) since they share a handler with a
 * VERIFIED sibling. Two real, VERIFIED opcodes have no behaviour to port:
 * `0x0230 FADE CURRENT MUSIC` and `0x2010 SET FRAME` are not implemented in
 * dgds either (both fall through to `ttm.cpp`'s own
 * `default: warning("Unimplemented TTM opcode...")` case). `0x0230`'s name
 * and zero-arg shape (mirroring `0x0220 STOP CURRENT MUSIC` exactly) is
 * unambiguous enough to give it a real command; `0x2010`'s "frame" is not —
 * it surfaces as `{kind:"unknown"}`. Every opcode not in the census
 * (REPORTED-only — never observed in real Red Baron data) also falls
 * through to `{kind:"unknown"}`, per the disassembler's own "never throw"
 * stance.
 */

import type { TtmScript } from "./ttm.ts";
import { handleInstruction, type TickState } from "./ttm-interpreter-ops.ts";
import { FULL_SCREEN_CLIP, MS_PER_FRAME, NULL_TTM_ENV, type TtmCommand, type TtmEnv, type TtmRunState, type TtmTickResult } from "./ttm-interpreter-types.ts";

export type { TtmAssetMeta, TtmBufferId, TtmCommand, TtmEnv, TtmRect, TtmRunState, TtmSpriteFlip, TtmTickResult } from "./ttm-interpreter-types.ts";
export { FULL_SCREEN_CLIP, MS_PER_FRAME, NULL_TTM_ENV, TTM_SCREEN_HEIGHT, TTM_SCREEN_WIDTH } from "./ttm-interpreter-types.ts";

/** dgds's `TTMSeq::reset()` initial values: both draw colors start at `0xF` (white), every slot at 0, full-screen draw window, zero delay. */
export function createTtmRun(script: TtmScript, env: TtmEnv = NULL_TTM_ENV): TtmRunState {
  return {
    script,
    env,
    pc: 0,
    frameIndex: 0,
    done: script.instructions.length === 0,
    delayFrames: 0,
    brush: 0,
    bmpSlot: 0,
    palSlot: 0,
    songSlot: 0,
    sndSlot: 0,
    getPutSlot: 0,
    fg: 0xf,
    bg: 0xf,
    clip: FULL_SCREEN_CLIP,
    bmpSlotAsset: new Map(),
  };
}

/**
 * Runs instructions from `run.pc` up to and including the next `0x0FF0
 * FINISH FRAME` (or the end of the instruction list, if the stream doesn't
 * end on one — every real Red Baron `.TTM` does, but synthetic scripts
 * needn't), returning the commands produced and the updated state. Calling
 * `advance()` again after `run.done` is already `true` is a safe no-op.
 */
export function advance(run: TtmRunState): TtmTickResult {
  if (run.done) {
    return { run, commands: [], delayMs: run.delayFrames * MS_PER_FRAME, delayFrames: run.delayFrames, frameIndex: run.frameIndex, done: true };
  }

  const { script, env } = run;
  const instructions = script.instructions;
  const commands: TtmCommand[] = [];
  const state: TickState = {
    delayFrames: run.delayFrames,
    brush: run.brush,
    bmpSlot: run.bmpSlot,
    palSlot: run.palSlot,
    songSlot: run.songSlot,
    sndSlot: run.sndSlot,
    getPutSlot: run.getPutSlot,
    fg: run.fg,
    bg: run.bg,
    clip: run.clip,
    bmpSlotAsset: new Map(run.bmpSlotAsset),
  };

  let pc = run.pc;
  let finishedTick = false;
  while (pc < instructions.length && !finishedTick) {
    const ins = instructions[pc]!;
    pc++;
    if (ins.op === 0x0ff0) {
      finishedTick = true; // the tick boundary itself, not a command
      continue;
    }
    const command = handleInstruction(state, env, ins);
    if (command) commands.push(command);
  }

  const done = pc >= instructions.length;
  const newRun: TtmRunState = { script, env, pc, frameIndex: run.frameIndex + 1, done, ...state };
  return { run: newRun, commands, delayMs: state.delayFrames * MS_PER_FRAME, delayFrames: state.delayFrames, frameIndex: run.frameIndex, done };
}

/** Convenience iterator: `createTtmRun` once, then `advance()` to completion, yielding each tick. */
export function* iterateTtmRun(script: TtmScript, env: TtmEnv = NULL_TTM_ENV): Generator<TtmTickResult, void, void> {
  let run = createTtmRun(script, env);
  while (!run.done) {
    const result = advance(run);
    yield result;
    run = result.run;
  }
}

/** Runs a script to completion and flattens every tick's commands into one array, in order. */
export function collectTtmCommands(script: TtmScript, env: TtmEnv = NULL_TTM_ENV): TtmCommand[] {
  const commands: TtmCommand[] = [];
  for (const tick of iterateTtmRun(script, env)) commands.push(...tick.commands);
  return commands;
}
